// Owner-side stamping page. Scanner-first: the camera is the page, the
// confirm step happens back on /dashboard so the camera is fully torn down
// while the owner is interacting with the customer's card (4b iteration —
// previously the camera stayed live and could double-trigger if its
// cooldown was bypassed). A small "Stamp without QR" link below the
// scanner swaps the body into a Code / Search form for the rare
// lookup-by-text case.
//
// Backend has accepted owner-role on POST /api/cards/:cardId/stamps since
// 4a-4 (added for exactly this counter-use case per CLAUDE.md).

import { lazy, Suspense, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { useBranches } from '../hooks/useBranches.js'
import { ownerApi } from '../ownerApi.js'
import { ScannerErrorBoundary } from '../components/ScannerErrorBoundary.js'
import type { StampCard } from '../components/StampConfirmOverlay.js'
import type { MembershipScan } from '../components/QrScannerTab.js'
import {
  StampFallbackForm,
  type StampFallbackMode,
} from '../../shared/stamp/StampFallbackForm.js'
import './stampPage.css'

const QrScannerTab = lazy(() => import('../components/QrScannerTab.js'))

const LAST_BRANCH_KEY = 'naya:lastUsedBranchId'

export function StampPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [fallbackOpen, setFallbackOpen] = useState(false)
  const [fallbackTab, setFallbackTab] = useState<StampFallbackMode>('code')
  // True while we're pre-warming the destination overlay's card-detail query
  // for a card resolved via the code/search fallback. QR scans pre-warm this
  // same cache synchronously inside QrScannerTab before calling onResolved,
  // so this never fires for a scan — see the cache check below.
  const [resolving, setResolving] = useState(false)

  const branches = useBranches()
  const branchList = branches.data?.branches ?? []
  const multiBranch = branchList.length > 1
  const [branchId, setBranchId] = useState<string | null>(null)

  useEffect(() => {
    if (branchId || branchList.length === 0) return
    if (!multiBranch) {
      setBranchId(branchList[0]!.id)
      return
    }
    const remembered = localStorage.getItem(LAST_BRANCH_KEY)
    const found = remembered && branchList.some((b) => b.id === remembered)
    setBranchId(found ? remembered : branchList[0]!.id)
  }, [branchId, branchList, multiBranch])

  // Persist the active branchId — the overlay reads it on /dashboard.
  useEffect(() => {
    if (branchId) localStorage.setItem(LAST_BRANCH_KEY, branchId)
  }, [branchId])

  async function onCardResolved(card: StampCard) {
    // A QR scan already pre-warmed this exact cache entry (synchronously, in
    // QrScannerTab, before calling this). The code/search fallback never
    // does — so only fetch here when the cache is actually cold, and show
    // the resolving spinner for exactly that window. This keeps the scan
    // path's "instant open, no flash" behavior while giving the fallback
    // paths the same warm-cache guarantee (and a visible loading state)
    // instead of racing the overlay's own query against navigation.
    const detailKey = ['owner', 'card-detail', card.card_id]
    if (!queryClient.getQueryData(detailKey)) {
      setResolving(true)
      try {
        await queryClient.fetchQuery({
          queryKey: detailKey,
          queryFn: () =>
            ownerApi.get(`/cards/${encodeURIComponent(card.card_id)}`),
        })
      } catch {
        // Non-fatal — the overlay's own query will retry and surface the
        // error there instead of stranding the owner on this page.
      }
      setResolving(false)
    }
    // Navigate back to the dashboard with the resolved card in router state.
    // The dashboard mounts <StampConfirmOverlay> when it sees this state and
    // clears it on close. Tearing down the scanner here guarantees no
    // double-trigger from a still-visible QR.
    navigate('/dashboard', { state: { stampCard: card } })
  }

  // SLICE-MEMBERSHIP-PASS — a membership scan carries the raw token to the
  // dashboard, which mounts <MembershipVerifyPanel> and POSTs the check-in.
  // Same scanner-teardown rationale as the loyalty path above.
  function onMembershipScanned(scan: MembershipScan) {
    navigate('/dashboard', {
      state: { membershipScan: { ...scan, branch_id: branchId } },
    })
  }

  function changeBranch(id: string) {
    setBranchId(id)
    localStorage.setItem(LAST_BRANCH_KEY, id)
  }

  return (
    <div className="stamp-page">
      <header className="stamp-header">
        <h1>Stamp</h1>
        {multiBranch && branchId && (
          <BranchPill
            branchId={branchId}
            branchList={branchList}
            onChange={changeBranch}
          />
        )}
      </header>

      {fallbackOpen ? (
        <StampFallbackForm
          mode={fallbackTab}
          onModeChange={setFallbackTab}
          onResolved={onCardResolved}
          onBack={() => setFallbackOpen(false)}
          searchPath="/owner/customers/search"
        />
      ) : (
        <>
          <ScannerErrorBoundary>
            <Suspense fallback={<ScannerSkeleton />}>
              <QrScannerTab
                paused={false}
                onResolved={onCardResolved}
                onMembership={onMembershipScanned}
              />
            </Suspense>
          </ScannerErrorBoundary>

          <button
            type="button"
            className="stamp-fallback-link"
            onClick={() => setFallbackOpen(true)}
          >
            Can't scan? Type a code or search →
          </button>
        </>
      )}

      {resolving && (
        <div className="stamp-page-resolving" aria-hidden="true">
          <span className="stamp-spinner-square stamp-spinner-square--lg" />
          <span className="stamp-scanner-captured-label">Opening card…</span>
        </div>
      )}
    </div>
  )
}

function BranchPill({
  branchId,
  branchList,
  onChange,
}: {
  branchId: string
  branchList: Array<{ id: string; name: string }>
  onChange: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const current = branchList.find((b) => b.id === branchId)
  return (
    <div className="stamp-branch-pill-wrap">
      <button
        type="button"
        className="stamp-branch-pill"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="stamp-branch-pill-dot" aria-hidden="true" />
        {current?.name ?? 'Branch'}
        <span className="stamp-branch-pill-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <>
          <div
            className="stamp-branch-backdrop"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <ul className="stamp-branch-menu" role="listbox">
            {branchList.map((b) => (
              <li key={b.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={b.id === branchId}
                  className={`stamp-branch-option${
                    b.id === branchId ? ' is-selected' : ''
                  }`}
                  onClick={() => {
                    onChange(b.id)
                    setOpen(false)
                  }}
                >
                  {b.name}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

function ScannerSkeleton() {
  return <div className="stamp-scanner-skeleton" aria-hidden="true" />
}
